/**
 * Resolução da empresa pelo subdomínio: `<slug>.makila.sistemamz.com`.
 *
 * A SPA é servida do mesmo pacote no domínio principal e em cada subdomínio de
 * empresa. O domínio-base vem de /api/public/web-config (não é conhecido em
 * tempo de compilação — varia entre ambientes), e o slug é o rótulo à frente
 * dele. No domínio principal (ou em localhost) não há slug.
 */
import { api } from './api';

interface WebConfig { baseHost: string; fiscalCert: string | null }
let webConfigCache: Promise<WebConfig> | null = null;

/** Configuração pública (um pedido por carregamento da página). */
export function getWebConfig(): Promise<WebConfig> {
  webConfigCache ??= api<WebConfig>('GET', '/public/web-config').catch((e) => { webConfigCache = null; throw e; });
  return webConfigCache;
}

async function baseHost(): Promise<string> {
  return (await getWebConfig()).baseHost.toLowerCase();
}

/** Domínio-base da aplicação (ex.: `makila.sistemamz.com`), para mostrar ao lado
 *  do campo de endereço no registo. */
export const getBaseHost = baseHost;

/** Tipo de host: o portal (apex), a consola de administração, ou uma empresa. */
export type HostKind = 'portal' | 'admin' | 'company';

/** Classifica o host actual. Em localhost (dev, sem subdomínios) assume-se o
 *  modo de empresa, para não partir o ambiente de desenvolvimento. */
export async function resolveHost(): Promise<{ kind: HostKind; slug: string | null }> {
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return { kind: 'company', slug: null };
  const base = (await baseHost()).split(':')[0]!; // sem porta
  if (host === base) return { kind: 'portal', slug: null };
  const suffix = '.' + base;
  if (host.endsWith(suffix)) {
    const label = host.slice(0, -suffix.length);
    if (label === 'admin') return { kind: 'admin', slug: null };
    if (/^[a-z0-9][a-z0-9-]{0,62}$/.test(label)) return { kind: 'company', slug: label };
  }
  return { kind: 'company', slug: null }; // domínio desconhecido (preview)
}

/** Slug da empresa no host actual, ou null se não for um subdomínio de empresa. */
export async function tenantSlugFromHost(): Promise<string | null> {
  return (await resolveHost()).slug;
}

export interface TenantBrand { slug: string; legalName: string; tradeName: string | null; brandColor: string | null }

/** Marca pública de uma empresa pelo slug (para o ecrã de entrada). */
export async function fetchTenantBrand(slug: string): Promise<TenantBrand | null> {
  try {
    return await api<TenantBrand>('GET', `/public/tenant/${encodeURIComponent(slug)}`);
  } catch {
    return null;
  }
}
